import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Botao, Cartao, CartaoTopo, Selo, Medidor, Tabela, Vazio, Avatar, type Coluna } from "@notes/ui";
import { Users, TrendingDown, TrendingUp, MessageSquareWarning, Clock, Mic, ListChecks, Coins } from "lucide-react";
import { Pagina, Secao, Numeros, Carregando, Erro } from "../shell/Shell.js";
import { trpc, mensagemDeErro } from "../lib/api.js";
import { reais, dataCurta } from "../lib/formato.js";

const PERIODOS = [
  { chave: 30, rotulo: "30 dias" },
  { chave: 90, rotulo: "90 dias" },
  { chave: 180, rotulo: "6 meses" },
] as const;

type Consultor = {
  consultorId: string; reunioes: number; media: number; evolucao: number;
  maiorPerda: { chave: string; rotulo: string; media: number } | null;
};

/**
 * EQUIPE. O painel de quem treina o time.
 *
 * A ordem é a ordem da decisão: primeiro o que a agência INTEIRA erra (treinar
 * um tema rende mais que corrigir uma pessoa), depois quem precisa de atenção,
 * depois o que os melhores fazem diferente, e por fim o que o cliente levanta e
 * ninguém trata.
 */
export function Gestao() {
  const [dias, setDias] = useState<number>(90);
  const q = useQuery({ queryKey: ["gestao", dias], queryFn: () => trpc.gestao.painel.query({ dias }) });

  if (q.isLoading) return <Pagina titulo="Equipe"><Carregando tipo="cartoes" /></Pagina>;
  if (q.isError) {
    return <Pagina titulo="Equipe"><Erro mensagem={mensagemDeErro(q.error)} aoTentarDeNovo={() => void q.refetch()} /></Pagina>;
  }
  const d = q.data!;

  const seletor = (
    <div className="enotes-chips" role="group" aria-label="Período">
      {PERIODOS.map((p) => (
        <button key={p.chave} className="enotes-chip" data-ativo={dias === p.chave || undefined}
          onClick={() => setDias(p.chave)}>{p.rotulo}</button>
      ))}
    </div>
  );

  if (d.reunioesAvaliadas === 0) {
    return (
      <Pagina titulo="Equipe" descricao="Como a agência conversa com o cliente." acao={seletor}>
        <Vazio titulo="Ainda não há conversa avaliada neste período"
          descricao="Os números aparecem quando as primeiras reuniões forem processadas e avaliadas." />
      </Pagina>
    );
  }

  const colunas: Array<Coluna<Consultor>> = [
    {
      chave: "pessoa", cabecalho: "Pessoa", ordenarPor: (l) => l.consultorId,
      celula: (l) => (
        <span className="enotes-tb-pessoa">
          <Avatar nome={l.consultorId} tamanho={24} />
          <span className="num">{l.consultorId.slice(0, 8)}</span>
        </span>
      ),
    },
    { chave: "conversas", cabecalho: "Conversas", largura: "7rem", alinhamento: "fim",
      ordenarPor: (l) => l.reunioes, celula: (l) => <span className="num">{l.reunioes}</span> },
    {
      chave: "media", cabecalho: "Média", largura: "12rem", ordenarPor: (l) => l.media,
      celula: (l) => <Medidor valor={l.media} rotulo="" tom={l.media >= 80 ? "bom" : l.media >= 60 ? "atencao" : "critico"} />,
    },
    {
      chave: "evolucao", cabecalho: "Evolução", largura: "8rem", alinhamento: "fim", ordenarPor: (l) => l.evolucao,
      celula: (l) => (
        <span className={`enotes-evolucao num ${l.evolucao < 0 ? "cai" : l.evolucao > 0 ? "sobe" : ""}`}>
          {l.evolucao > 0 ? <TrendingUp size={12} aria-hidden /> : l.evolucao < 0 ? <TrendingDown size={12} aria-hidden /> : null}
          {l.evolucao > 0 ? `+${l.evolucao}` : l.evolucao}
        </span>
      ),
    },
    {
      chave: "treinar", cabecalho: "Onde treinar", ordenarPor: (l) => l.maiorPerda?.rotulo ?? "",
      celula: (l) => l.maiorPerda ? <Selo tom="atencao">{l.maiorPerda.rotulo}</Selo> : <span className="carimbo">sem registro</span>,
    },
  ];

  return (
    <Pagina titulo="Equipe" descricao="Como a agência conversa com o cliente." largura="larga" acao={seletor}>
      <Secao ordem={0}>
        <Numeros itens={[
          { rotulo: "Conversas", valor: d.numeros.reunioes, icone: <Mic size={13} /> },
          { rotulo: "Horas ouvidas", valor: d.numeros.horas, icone: <Clock size={13} /> },
          { rotulo: "Promessas vencidas", valor: d.numeros.promessasVencidas, icone: <ListChecks size={13} />,
            alerta: d.numeros.promessasVencidas > 0, nota: `${d.numeros.promessasAbertas} em aberto` },
          { rotulo: "Custo do período", valor: reais(d.numeros.custoCents), icone: <Coins size={13} /> },
        ]} />
      </Secao>

      <Secao titulo="Onde a agência mais perde ponto" ordem={1}>
        <Cartao>
          <CartaoTopo icone={<TrendingDown size={15} />} titulo="Lacunas da equipe"
            descricao="Treinar um tema rende mais que corrigir uma pessoa." />
          {d.lacunas.length === 0 ? <Vazio titulo="Sem dado suficiente" /> : (
            <ul className="enotes-lacunas">
              {d.lacunas.map((l) => (
                <li key={l.chave}>
                  <Medidor valor={l.media * 10} rotulo={l.rotulo} tom={l.media < 6 ? "critico" : "atencao"} mostrarNumero={false} />
                  <span className="num enotes-lacuna-media">{l.media}</span>
                  <span className="carimbo">{l.consultoresAbaixo} pessoa{l.consultoresAbaixo === 1 ? "" : "s"} abaixo</span>
                </li>
              ))}
            </ul>
          )}
        </Cartao>
      </Secao>

      <Secao titulo="Consultores" ordem={2}>
        <Tabela colunas={colunas} linhas={d.ranking as Consultor[]} chave={(l) => l.consultorId}
          vazio={<Vazio titulo="Ninguém avaliado neste período" />} />
      </Secao>

      <div className="enotes-duas-colunas">
        <Secao titulo="O que os melhores fazem diferente" ordem={3}>
          <Cartao>
            {!d.comparacaoConfiavel ? (
              <Vazio titulo="Ainda não dá para comparar"
                descricao="Com poucos consultores avaliados, a diferença seria ruído, e mudar o método por ruído é pior que não mudar." />
            ) : (
              <ul className="enotes-comparacao">
                {d.melhores.map((m) => (
                  <li key={m.chave}>
                    <span className="enotes-comparacao-rotulo">{m.rotulo}</span>
                    <span className="enotes-comparacao-numeros num">
                      <strong>{m.mediaDosMelhores}</strong> contra {m.mediaDosDemais}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Cartao>
        </Secao>

        <Secao titulo="O que o cliente levanta" ordem={4}>
          <Cartao>
            <CartaoTopo icone={<MessageSquareWarning size={15} />} titulo="Voz do cliente"
              descricao="Tema que aparece muito e é pouco tratado é treinamento esperando para acontecer." />
            {d.vozDoCliente.length === 0 ? <Vazio titulo="Nenhuma objeção registrada" /> : (
              <ul className="enotes-voz">
                {d.vozDoCliente.map((t) => {
                  const pct = Math.round(t.proporcaoTratada * 100);
                  return (
                    <li key={t.tema}>
                      <span className="enotes-voz-tema">{t.tema}</span>
                      <Selo tom={pct < 50 ? "critico" : "bom"}>{pct}% tratada</Selo>
                      <span className="carimbo num">{t.vezes} vez{t.vezes === 1 ? "" : "es"}</span>
                      <span className="carimbo">última em {dataCurta(t.ultimaVez)}</span>
                    </li>
                  );
                })}
              </ul>
            )}
          </Cartao>
        </Secao>
      </div>
    </Pagina>
  );
}
